import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Search, X, SlidersHorizontal } from "lucide-react";
import { CATEGORY_SLUGS, CATEGORY_LABELS_I18N } from "@/api/listings";

const CAT_LABEL = { nl: "Categorie", de: "Kategorie", en: "Category" };
const ALL_LABEL = { nl: "Alle", de: "Alle", en: "All" };
const CLEAR_LABEL = { nl: "Filters wissen", de: "Filter zurücksetzen", en: "Clear filters" };
const SEARCH_PLACEHOLDER = {
  nl: "Zoek een bedrijf op naam...",
  de: "Suche ein Unternehmen nach Name...",
  en: "Search for a business by name...",
};
const HIDE_FILTERS = { nl: "Verberg filters", de: "Filter ausblenden", en: "Hide filters" };

export const DirectoryFilters = ({
  q,
  setQ,
  category,
  setCategory,
  // `soort`/`setSoort` blijven optioneel om callers niet te breken, maar worden niet meer gerenderd.
  soort: _soort,
  setSoort: _setSoort,
  facets,
  lockedCategory = null,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "nl";
  const [mobileOpen, setMobileOpen] = useState(false);
  const hasActiveFilter = !!(category && !lockedCategory);

  const clearAll = () => {
    setQ("");
    if (!lockedCategory) setCategory("");
  };

  // Bouw pill-config: vaste 8 categorieën in vaste volgorde, count uit facets (0 als ontbrekend).
  // Multi-category listings tellen mee in elke gekozen categorie (backend unwindt op `categories`).
  const countByKey = useMemo(() => {
    const map = {};
    for (const c of facets.categories || []) map[c.key] = c.count;
    return map;
  }, [facets.categories]);

  const pills = useMemo(
    () =>
      CATEGORY_SLUGS.map((slug) => ({
        key: slug,
        label: CATEGORY_LABELS_I18N[slug]?.[lang] || CATEGORY_LABELS_I18N[slug]?.nl || slug,
        count: countByKey[slug] || 0,
      })),
    [countByKey, lang],
  );

  const totalCount = facets.total ?? 0;

  return (
    <div data-testid="directory-filters" className="mb-8">
      {/* Zoekbalk */}
      <div className="bg-white rounded-full shadow-md flex items-center pl-5 pr-2 py-2 mb-4 border border-[#E4EEF5]">
        <Search className="w-5 h-5 text-[#2E9CD6] shrink-0" />
        <input
          data-testid="directory-search"
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={SEARCH_PLACEHOLDER[lang] || SEARCH_PLACEHOLDER.nl}
          className="flex-1 px-3 py-2 text-[#173A5E] placeholder:text-[#8AA3B8] font-[Inter] text-sm sm:text-base bg-transparent outline-none min-w-0"
          aria-label={t("common.zoeken")}
        />
        {q && (
          <button
            data-testid="clear-search"
            onClick={() => setQ("")}
            className="p-2 text-[#8AA3B8] hover:text-[#173A5E] transition"
            aria-label={CLEAR_LABEL[lang] || CLEAR_LABEL.nl}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Mobile filter toggle */}
      <div className="sm:hidden mb-3">
        <button
          data-testid="mobile-filter-toggle"
          onClick={() => setMobileOpen((s) => !s)}
          className="w-full inline-flex items-center justify-center gap-2 bg-white border border-[#E4EEF5] rounded-full px-4 py-2.5 font-[Poppins] font-semibold text-sm text-[#173A5E]"
        >
          <SlidersHorizontal className="w-4 h-4" />
          {mobileOpen ? (HIDE_FILTERS[lang] || HIDE_FILTERS.nl) : t("common.filter")}
          {hasActiveFilter && (
            <span className="ml-1 w-2 h-2 rounded-full bg-[#2E9CD6]" />
          )}
        </button>
      </div>

      {/* Categorie-pills — alleen op de globale bedrijvengids (niet op vaste categorie-pagina's). */}
      {!lockedCategory && (
        <div className={`${mobileOpen ? "block" : "hidden"} sm:block`}>
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <span className="text-xs font-[Poppins] font-semibold uppercase tracking-wider text-[#5A7594]">
              {CAT_LABEL[lang] || CAT_LABEL.nl}
            </span>
            {(hasActiveFilter || q) && (
              <button
                data-testid="filter-clear"
                onClick={clearAll}
                className="inline-flex items-center gap-1.5 text-xs font-[Poppins] font-semibold text-[#CE1B2A] hover:text-[#a51420] transition"
              >
                <X className="w-3.5 h-3.5" />
                {CLEAR_LABEL[lang] || CLEAR_LABEL.nl}
              </button>
            )}
          </div>
          <div
            data-testid="category-pills"
            className="flex flex-wrap gap-2"
            role="group"
            aria-label={CAT_LABEL[lang] || CAT_LABEL.nl}
          >
            {/* Alle-pill */}
            <button
              type="button"
              data-testid="category-pill-all"
              onClick={() => setCategory("")}
              aria-pressed={!category}
              className={pillClasses(!category)}
            >
              {ALL_LABEL[lang] || ALL_LABEL.nl}
              <span className={countClasses(!category)}>{totalCount}</span>
            </button>
            {pills.map((p) => {
              const active = category === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  data-testid={`category-pill-${p.key}`}
                  onClick={() => setCategory(active ? "" : p.key)}
                  aria-pressed={active}
                  className={pillClasses(active)}
                >
                  {p.label}
                  <span className={countClasses(active)}>{p.count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// -- helpers --
const pillClasses = (active) =>
  `inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-[Poppins] font-semibold transition border ${
    active
      ? "bg-[#173A5E] text-white border-[#173A5E] shadow-sm"
      : "bg-white text-[#173A5E] border-[#E4EEF5] hover:border-[#6BC0E8]"
  }`;

const countClasses = (active) =>
  `text-[10px] ${active ? "text-white/80" : "text-[#5A7594]"}`;

export default DirectoryFilters;
